// DashboardFilterModal.jsx
import { useState, useEffect, useRef } from "react";
import { FaTimes, FaCalendarAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentDate } from "../../reduxtoolkit/filterslice";
import { customObjectToDateString } from "../../dateutils/date";
import { styled } from "@mui/material/styles";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import api from "../../api/api";
import dayjs from "dayjs";
import { actionDropDownCalenderIcon } from "./svgs";
import { dashboardFilterShown, getPracticeId } from "../../config/practice";

const CustomDateCalendar = styled(DateCalendar)(({ theme }) => ({
  "& .MuiPickersCalendarHeader-label": { fontSize: "15px" },
}));

const DashboardFilterModal = ({
  isOpen,
  onClose,
  fetchDashboardData,
  selectedFilterName,
  setSelectedFilterName,
  appliedFilter,
  setAppliedFilter,
}) => {
  const dispatch = useDispatch();
  const calendarRef = useRef(null);
  const practiseid = getPracticeId();

  const [selectedDate, setSelectedDate] = useState(null);
  const [tempSelectedDate, setTempSelectedDate] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const [providerList, setProviderList] = useState([]);
  const [visitStatusList, setVisitStatusList] = useState([]);

  const [selectedFilters, setSelectedFilters] = useState({
    status: [],
    providers: [],
    orderStatus: null,
    visitStatus: [],
    category: null,
    rpm: null,
    raisedIssue: null,
    levelOfVisit: null,
    acoConditions: null,
    insuranceName: null,
  });

  const currentDate = useSelector((state) => state.filter.currentDate);

  useEffect(() => {
    if (isOpen) {
      const dateToUse = currentDate ? dayjs(currentDate) : dayjs();
      setTempSelectedDate(dateToUse);
    }
  }, [isOpen, currentDate]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    }

    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCalendarOpen]);

  // Initialize filters after provider and visit status lists are loaded
  useEffect(() => {
    if (isOpen && selectedFilterName) {
      const mapping = { "Checked-in": "chk", Morning: "mng", Afternoon: "afr" };
      const reverseMapping = {
        chk: "Checked-in",
        mng: "Morning",
        afr: "Afternoon",
      };

      setSelectedFilters({
        status: (selectedFilterName.status || []).map(
          (s) => reverseMapping[s] || s
        ),
        providers: (selectedFilterName.providers || []).map((p) =>
          String(p.id)
        ),
        orderStatus: selectedFilterName.orderStatus || null,
        visitStatus: (selectedFilterName.visitStatus || []).map((v) =>
          String(v.id)
        ),
        category: selectedFilterName.category || null,
        rpm: selectedFilterName.rpm || null,
        raisedIssue: selectedFilterName.raisedIssue || null,
        levelOfVisit: selectedFilterName.levelOfVisit || null,
        acoConditions: selectedFilterName.acoConditions || null,
        insuranceName: selectedFilterName.insuranceName || null,
      });
    }
  }, [isOpen, selectedFilterName, providerList, visitStatusList]);

  const formatDateDot = (val) => {
    if (!val) return "";
    const d = dayjs.isDayjs(val) ? val : dayjs(val);
    return d.isValid() ? d.format("MM.DD.YYYY") : "";
  };

  useEffect(() => {
    if (!isOpen) return;

    const getProviders = async () => {
      try {
        const res = await api.get(`/v1/${practiseid}/providers`);
        if (res.status === 200 && Array.isArray(res.data))
          setProviderList(res.data);
      } catch {
        setProviderList([]);
      }
    };

    const getVisitStatuses = async () => {
      try {
        const res = await api.post(
          `/v1/${practiseid}/appointments/status-types`,
          {
            appointment_types: ["check-in", "visit confirmation"],
          }
        );
        if (res.status === 200 && Array.isArray(res.data))
          setVisitStatusList(res.data);
      } catch {
        setVisitStatusList([]);
      }
    };

    getProviders();
    getVisitStatuses();
  }, [isOpen, practiseid]);

  const handleTempDateChange = (date) => {
    setTempSelectedDate(date);
    setIsCalendarOpen(false);
  };

  const handleFilterClick = (group, value, multiSelect = false) => {
    const stringValue = String(value);
    if (multiSelect) {
      setSelectedFilters((prev) => {
        const arr = prev[group] || [];
        return {
          ...prev,
          [group]: arr.includes(stringValue)
            ? arr.filter((v) => v !== stringValue)
            : [...arr, stringValue],
        };
      });
    } else {
      setSelectedFilters((prev) => ({
        ...prev,
        [group]: prev[group] === stringValue ? null : stringValue,
      }));
    }
  };

  const buildSelectedLabels = () => ({
    status: [...selectedFilters.status],

    providers: selectedFilters.providers.map((id) => ({
      id: String(id),
      name:
        providerList.find((p) => String(p.providerid) === String(id))
          ?.providername ?? String(id),
    })),

    orderStatus: selectedFilters.orderStatus || null,

    visitStatus: selectedFilters.visitStatus.map((id) => ({
      id: String(id),
      name:
        visitStatusList.find((v) => String(v.id) === String(id))?.value ??
        String(id),
    })),

    category: selectedFilters.category || null,
    rpm: selectedFilters.rpm || null,
    raisedIssue: selectedFilters.raisedIssue || null,
    levelOfVisit: selectedFilters.levelOfVisit || null,
    acoConditions:
      selectedFilters.acoConditions === "Aco Conditions"
        ? "Aco Conditions"
        : null,
    insuranceName: selectedFilters.insuranceName || null,
  });

  const handleApply = () => {
    setSelectedDate(tempSelectedDate);
    dispatch(
      setCurrentDate(
        tempSelectedDate && customObjectToDateString(tempSelectedDate)
      )
    );
    setIsCalendarOpen(false);

    const mapping = { "Checked-in": "chk", Morning: "mng", Afternoon: "afr" };
    const filterObject = { stype: "1" };

    if (tempSelectedDate)
      filterObject.filterdate = customObjectToDateString(tempSelectedDate);
    if (selectedFilters.status.length)
      filterObject.status = selectedFilters.status.map((s) => mapping[s]);
    if (selectedFilters.providers.length)
      filterObject.provider = selectedFilters.providers.map(String);
    if (selectedFilters.visitStatus.length)
      filterObject.visit = [...selectedFilters.visitStatus];
    if (selectedFilters.levelOfVisit)
      filterObject.lovstatus = selectedFilters.levelOfVisit;

    if (selectedFilters.orderStatus) {
      const orderStatusMapping = {
        "Active-in Office": "active-in-office",
        Active: "active",
        Inactive: "inactive",
      };
      filterObject.statusNotes =
        orderStatusMapping[selectedFilters.orderStatus];
    }

    if (selectedFilters.category) {
      filterObject.afcategory = [selectedFilters.category];
    }

    if (selectedFilters.rpm === "Enrolled") {
      filterObject.rpmstatus = "Y";
    }
 if (selectedFilters.rpm === "Not Enrolled") {
      filterObject.rpmstatus = "N";
    }
    if (selectedFilters.acoConditions === "Aco Conditions") {
      filterObject.acostatus = "Y";
    }

    if (selectedFilters.insuranceName) {
      filterObject.insurancename = selectedFilters.insuranceName;
    }

    if (selectedFilters.raisedIssue === "Red Flag") {
      filterObject.issueflagstatus = "Y";
    } else if (selectedFilters.raisedIssue === "Green Flag") {
      filterObject.issueflagstatus = "N";
    }

    // Build the labels for display
    const labels = buildSelectedLabels();
    if (tempSelectedDate) {
      labels.date = formatDateDot(tempSelectedDate);
    }

    // Update the parent component's state
    setAppliedFilter?.(filterObject);
    setSelectedFilterName?.(labels);

    fetchDashboardData({ 
      searchflag: "", 
      filter: filterObject,
      labels: labels
    });
    onClose();
    console.log("Final Filter Object:", filterObject);
    console.log("Final Labels:", labels);
  };

  const handleClear = () => {
    const today = dayjs();
    setTempSelectedDate(today);
    setSelectedDate(today);
    dispatch(setCurrentDate(today.format("YYYY-MM-DD")));
    setSelectedFilters({
      status: [],
      providers: [],
      orderStatus: null,
      visitStatus: [],
      category: null,
      rpm: null,
      raisedIssue: null,
      levelOfVisit: null,
      acoConditions: null,
      insuranceName: null,
    });

    // Clear the parent component's state
    setAppliedFilter?.({});
    setSelectedFilterName?.({});
    
    fetchDashboardData({ searchflag: "", filter: "", labels: {} });
    setIsCalendarOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  // ——— UI helpers (chips) ———
  const Chip = ({ active, children, onClick }) => (
    <button
      onClick={onClick}
      className={`px-3 py-2 rounded-full text-sm transition  focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none
      ${
        active
          ? "bg-[#E88D67] shadow-md shadow-[#E88D6733] text-white"
          : "bg-[#F9FAFB] text-[#374151] hover:bg-[#EDEDED] border border-[#E5E7EB]"
      }`}
    >
      {children}
    </button>
  );

  const Section = ({ title, children }) => (
    <div>
      <div className="text-[14px] font-font1 text-left font-medium text-[#111827] mb-2">
        {title}
      </div>
      {children}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-3">
      <div className="w-full max-w-[620px] mt-5 h-auto  bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="relative px-4 py-2 mt-2 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] font-semibold text-[#111827]">Filter</h2>
            <button
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
              onClick={onClose}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 custom-scrollbar w-full max-h-[70vh] space-y-4">
          <div className=" flex">
            <button
              type="button"
              onClick={() => setIsCalendarOpen((v) => !v)}
              className="inline-flex items-center gap-2 px-2.5 py-2 rounded-full border-[1px] border-[#E5E7EB] text-[#111827] bg-[#F9FAFB]"
            >
              {/* <FaCalendarAlt className="text-[#6B7280]" /> */}
              <div className="text-[#374151]">{actionDropDownCalenderIcon}</div>
              <span className="text-sm font-font1 font-medium leading-5 text-[#374151]">
                {tempSelectedDate
                  ? formatDateDot(tempSelectedDate)
                  : "Select date"}
              </span>
            </button>

            {isCalendarOpen && (
              <div
                ref={calendarRef}
                className="absolute z-10 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg"
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <CustomDateCalendar
                    value={tempSelectedDate}
                    onChange={handleTempDateChange}
                  />
                </LocalizationProvider>
              </div>
            )}
          </div>

          {dashboardFilterShown("Status") && (
          <Section title="Status">
            <div className="flex flex-wrap gap-2">
              {["Checked-in", "Morning", "Afternoon"].map((item) => (
                <Chip
                  key={item}
                  active={selectedFilters.status.includes(item)}
                  onClick={() => handleFilterClick("status", item, true)}
                >
                  {item}
                </Chip>
              ))}
            </div>
          </Section>
          )}

          {dashboardFilterShown("Providers") && (
          <Section title="Providers">
            <div className="flex flex-wrap gap-2">
              {providerList.map((p) => (
                <Chip
                  key={p.providerid}
                  active={selectedFilters.providers.includes(
                    String(p.providerid)
                  )}
                  onClick={() =>
                    handleFilterClick("providers", p.providerid, true)
                  }
                >
                  {p.providername}
                </Chip>
              ))}
            </div>
          </Section>
          )}

          {dashboardFilterShown("Order Status") && (
          <Section title="Order Status">
            <div className="flex flex-wrap gap-2">
              {["Active", "Inactive", "Active-in Office"].map((item) => (
                <Chip
                  key={item}
                  active={selectedFilters.orderStatus === item}
                  onClick={() => handleFilterClick("orderStatus", item)}
                >
                  {item}
                </Chip>
              ))}
            </div>
          </Section>
          )}

          {dashboardFilterShown("Visit Status") && (
          <Section title="Visit Status">
            <div className="flex flex-wrap gap-2">
              {visitStatusList.map((v) => (
                <Chip
                  key={v.id}
                  active={selectedFilters.visitStatus.includes(String(v.id))}
                  onClick={() => handleFilterClick("visitStatus", v.id, true)}
                >
                  {v.value}
                </Chip>
              ))}
            </div>
          </Section>
          )}

          {/* Category & RPM (two columns on md+) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {dashboardFilterShown("Category") && (
            <Section title="Category">
              <div className="flex flex-wrap gap-2">
                {["Labs", "Procedures"].map((item) => (
                  <Chip
                    key={item}
                    active={selectedFilters.category === item}
                    onClick={() => handleFilterClick("category", item)}
                  >
                    {item}
                  </Chip>
                ))}
              </div>
            </Section>
            )}

            {dashboardFilterShown("RPM") && (
            <Section title="RPM">
              <div className="flex flex-wrap gap-2">
                {["Enrolled", "Not Enrolled"].map((item) => (
                  <Chip
                    key={item}
                    active={selectedFilters.rpm === item}
                    onClick={() => handleFilterClick("rpm", item)}
                  >
                    {item}
                  </Chip>
                ))}
              </div>
            </Section>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {dashboardFilterShown("Raised Issue's") && (
            <Section title="Raised Issue's">
              <div className="flex flex-wrap gap-2">
                {["Red Flag", "Green Flag"].map((item) => (
                  <Chip
                    key={item}
                    active={selectedFilters.raisedIssue === item}
                    onClick={() => handleFilterClick("raisedIssue", item)}
                  >
                    {item}
                  </Chip>
                ))}
              </div>
            </Section>
            )}

            {dashboardFilterShown("Level of Visit") && (
            <Section title="Level of Visit">
              <div className="flex flex-wrap gap-2">
                {["Yes", "No"].map((item) => (
                  <Chip
                    key={item}
                    active={selectedFilters.levelOfVisit === item}
                    onClick={() => handleFilterClick("levelOfVisit", item)}
                  >
                    {item}
                  </Chip>
                ))}
              </div>
            </Section>
            )}

          </div>

          {dashboardFilterShown("Insurance Name") && (
          <Section title="Insurance Name">
            <div className="flex flex-wrap gap-2">
              {["ACO", "WellMed", "United Healthcare"].map((item) => (
                <Chip
                  key={item}
                  active={selectedFilters.insuranceName === item}
                  onClick={() => handleFilterClick("insuranceName", item)}
                >
                  {item}
                </Chip>
              ))}
            </div>
          </Section>
          )}

          {/* ACO Conditions */}
          {dashboardFilterShown("Aco Conditions") && (
          <Section title="Aco Conditions">
            <div className="flex flex-wrap gap-1">
              <Chip
                active={selectedFilters.acoConditions === "Aco Conditions"}
                onClick={() =>
                  handleFilterClick("acoConditions", "Aco Conditions")
                }
              >
                Aco Conditions
              </Chip>
            </div>
          </Section>
          )}
        </div>

        <div className="px-4 py-3 border-t bg-[#F4F4F5] flex items-center justify-center gap-3">
          <button
            className="py-2 w-[50%] rounded-[10px] border border-gray-300 bg-white text-[#374151] hover:bg-gray-50"
            onClick={handleClear}
          >
            Clear
          </button>
          <button
            className="w-[50%] py-2 rounded-[10px] bg-black text-white hover:opacity-90"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardFilterModal;
